import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Departures, SalesTypes } from 'app/core/enums/common.enum';
import { FinanceResources } from 'app/core/security/resources/resources';
import { VenueFinancesService } from 'app/modules/sho_management_pro/venue/services/venue-finances.service';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { AdapterService } from '../../services/adapters/adapter.service';
import { OtherSalesService } from '../../services/sales/other-sales/other-sales.service';
import { TotalGrossService } from '../../services/sales/total-gross.service';
import * as OtherSalesActions from './other-sales.actions';

@Injectable()
export class OtherSalesEffects {
    private _actions$ = inject(Actions);
    private _saleService = inject(OtherSalesService);
    private _alertService = inject(AlertService);
    private _venueFinancesService = inject(VenueFinancesService);
    private _totalGrossService = inject(TotalGrossService);
    private adapter = inject(AdapterService);

    messages = {
        getAllError: 'Error getting all Other Sales',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Other Sales
    getAllOtherSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OtherSalesActions.loadOtherSales),
            switchMap((action) => {
                return this._saleService.getAll(action.playdateId).pipe(
                    map((res) => {
                        const data = this.adapter.adaptPkOtSalesList(
                            res.data,
                            FinanceResources.OTHER
                        );

                        return OtherSalesActions.loadOtherSalesSuccess({
                            data: data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            OtherSalesActions.loadOtherSalesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create Other Sale
    createOtherSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OtherSalesActions.createOtherSale),
            switchMap((action) => {
                return this._saleService.create(action.data).pipe(
                    map((res) => {
                        const sale = this.adapter.adaptPkOtSale(
                            res.data,
                            FinanceResources.OTHER
                        );
                        return OtherSalesActions.createOtherSaleSuccess({
                            sale: sale,
                            success: 'Other Sale created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            OtherSalesActions.createOtherSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Other Sale
    updateOtherSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OtherSalesActions.updateOtherSale),
            switchMap((action) => {
                return this._saleService.update(action.data, action.id).pipe(
                    map((res) => {
                        const sale = this.adapter.adaptPkOtSale(
                            res.data,
                            FinanceResources.OTHER
                        );
                        return OtherSalesActions.updateOtherSaleSuccess({
                            data: sale,
                            success: 'Other Sale updated successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            OtherSalesActions.updateOtherSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Sale
    deleteOtherSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OtherSalesActions.deleteOtherSale),
            switchMap((action) => {
                return this._saleService.delete(action.payload).pipe(
                    map((res) => {
                        const data = res.data;
                        return OtherSalesActions.deleteOtherSaleSuccess({
                            deletedSales: data.deleted_sales,
                            remainingSales: data.remaining_sales,
                            success: 'Other Sale deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            OtherSalesActions.deleteOtherSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Sale Item
    deleteOtherSaleItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OtherSalesActions.deleteOtherSaleItem),
            switchMap((action) => {
                return this._saleService.delete(action.payload).pipe(
                    map((res) => {
                        const saleId = Number(res.data.deleted_sales[0]);
                        const totals = res.data.totals;

                        if (!saleId) {
                            const message =
                                'Sale item ID not found in response';
                            this._alertService.showAlert(message, 'error');
                            throw new Error(message);
                        }

                        return OtherSalesActions.deleteOtherSaleItemSuccess({
                            deletedSaleItem: saleId,
                            totals,
                            success: 'Other sale item deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            OtherSalesActions.deleteOtherSaleItemFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onOtherSaleActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    OtherSalesActions.createOtherSaleSuccess,
                    OtherSalesActions.updateOtherSaleSuccess,
                    OtherSalesActions.deleteOtherSaleSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');

                    this._venueFinancesService.onUpdateFinances = true;
                    this._totalGrossService.onValidateFinances(
                        Departures.OTHER,
                        SalesTypes.SALE_OTHERS,
                        FinanceResources.OTHER
                    );
                })
            ),
        { dispatch: false }
    );

    onOtherSaleActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    OtherSalesActions.loadOtherSalesFailure,
                    OtherSalesActions.loadOtherSaleFailure,
                    OtherSalesActions.createOtherSaleFailure,
                    OtherSalesActions.updateOtherSaleFailure,
                    OtherSalesActions.deleteOtherSaleFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
